import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, download } from './client';
import type { FormType } from './forms';
import type { Inspection } from './inspections';

export type DocumentFormat = 'pdf' | 'docx';
export type DocumentLanguage = 'sk' | 'en';

export interface ReportMetadata {
  id: string;
  inspectionNumber: string;
  generatedAt: string;
  generatedBy: string;
  sha256: string;
}

/** Text with an optional language override (a Slovak text in an English report: lang = 'sk'). */
export interface TextView {
  value: string;
  lang: string | null;
}

export interface SpanView {
  text: string;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  /** http(s) or mailto only (validated by the backend). */
  link: string | null;
}

export interface RichBlockView {
  kind: 'PARAGRAPH' | 'BULLET_LIST' | 'ORDERED_LIST';
  items: SpanView[][];
}

export interface CellView {
  lines: TextView[];
  /** Lines to fill in by hand (printable blank forms only). */
  blankLines: number;
  notes: TextView[];
  rich: RichBlockView[];
}

export interface TableView {
  /** null when the preceding heading names the table */
  caption: TextView | null;
  headers: TextView[];
  rows: CellView[][];
  rowHeaders: boolean;
  /** Relative column widths in percent (as in the PDF). */
  widths: number[];
}

/** One block of the document model; only the fields of its kind are set. */
export interface BlockView {
  kind: 'HEADING' | 'PARAGRAPH' | 'TABLE';
  level: number | null;
  text: TextView | null;
  style: 'NORMAL' | 'NOTE' | 'LEAD' | null;
  table: TableView | null;
}

/** The renderer-independent document model (same content as the PDF/DOCX). */
export interface DocumentView {
  title: string;
  lang: string;
  blocks: BlockView[];
}

export interface ReportView {
  report: ReportMetadata;
  document: DocumentView;
}

export function downloadFormDocument(inspectionId: string, formType: FormType, format: DocumentFormat, lang: DocumentLanguage) {
  return download(`/inspections/${inspectionId}/forms/${formType}/document`, { format, lang }, `${formType}_${lang}.${format}`);
}

export function downloadReport(inspectionId: string, format: DocumentFormat, lang: DocumentLanguage) {
  return download(`/inspections/${inspectionId}/report/document`, { format, lang }, `report_${lang}.${format}`);
}

export function useReport(inspectionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['inspections', inspectionId, 'report'],
    queryFn: () => api<ReportMetadata>(`/inspections/${inspectionId}/report`),
    enabled,
  });
}

/** The final report for display in the app, in the requested language (rendered from the frozen snapshot). */
export function useReportView(inspectionId: string | undefined, lang: DocumentLanguage) {
  return useQuery({
    queryKey: ['inspections', inspectionId, 'report', 'view', lang],
    queryFn: ({ signal }) => api<ReportView>(`/inspections/${inspectionId}/report/view`, { query: { lang }, signal }),
    enabled: !!inspectionId,
    placeholderData: (previous) => previous,
    staleTime: Infinity,
  });
}

/** Freezes the report snapshot and finishes the inspection. */
export function useGenerateReport(inspectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) => api<Inspection>(`/inspections/${inspectionId}/report`, { method: 'POST', body: { version }, version }),
    onSuccess: (inspection) => {
      qc.setQueryData(['inspections', inspectionId], inspection);
      void qc.invalidateQueries({ queryKey: ['inspections'] });
    },
  });
}
